/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useLocation } from "../lib/react-query/useLocation";
import { useServices } from "../lib/react-query/useServices";
import { useProvider } from "../lib/react-query/useProvider";
import useWidgetCallbackParams from "./useWidgetCallbackParams";

export const useSchedulerParams = () => {
  const { schedulerChannel, location: locationId, serviceIds, provider: providerId } = useWidgetCallbackParams();

  const { data: locationData, isLoading: isLocationLoading } = useLocation(locationId, {
    enabled: !!locationId,
  });

  const { data: servicesData, isLoading: isServicesLoading } = useServices(
    { "filter[mark_combine]": true },
    { enabled: serviceIds.length > 0 }
  );

  const { data: providerData, isLoading: isProviderLoading } = useProvider(providerId, {
    enabled: !!providerId,
  });

  const { serviceDataList, notFoundServiceIds } = useMemo(() => {
    if (!serviceIds.length || !servicesData) return { serviceDataList: [], notFoundServiceIds: [] };

    const byId = new Map(servicesData.map((s) => [+s.id, s]));
    const found = [];
    const notFound = [];
    serviceIds.forEach((raw) => {
      const service = byId.get(+raw);
      service ? found.push(service) : notFound.push(raw);
    });

    return { serviceDataList: found, notFoundServiceIds: notFound };
  }, [serviceIds, servicesData]);

  return {
    schedulerChannel,
    locationData,
    serviceDataList,
    notFoundServiceIds,
    providerData,
    hasLocationParam: !!locationId,
    hasProviderParam: !!providerId,
    isLoading: isLocationLoading || isServicesLoading || isProviderLoading,
  };
};
